import React from 'react';

// Build-time glob of every screen module. Vite's `import.meta.glob` needs a
// literal-prefix pattern (no template interpolation) so it can statically
// enumerate matching files at build time and emit a code-split chunk per
// route. This replaces the CRA-era `React.lazy(() => import(`screens/${...}`))`
// pattern that Vite refuses to analyze (the bare `screens/` specifier looks
// like a node_modules package to Vite's resolver).
//
// The keys are paths like `../screens/Dashboard.tsx` or
// `../screens/SignUp/index.ts` — both file-style and folder-with-index
// conventions exist in the codebase, so we probe both shapes per route.
const screenModules = import.meta.glob<{ default: React.ComponentType<unknown> }>(
  '../screens/**/*.{ts,tsx}',
);

/**
 * React.lazy wrapper that resolves `routes.<X>[i].componentPath` (a string
 * like `'Dashboard'` or `'SignUp/SignUpSuccess'`) to the actual screen module.
 * Falls back through the same resolution shapes webpack/CRA used to:
 *   1. `<path>.tsx`
 *   2. `<path>/index.tsx`
 *   3. `<path>/index.ts`
 *   4. `<path>.ts`
 */
export const lazyScreen = (componentPath: string) => React.lazy(async () => {
  const candidates = [
    `../screens/${componentPath}.tsx`,
    `../screens/${componentPath}/index.tsx`,
    `../screens/${componentPath}/index.ts`,
    `../screens/${componentPath}.ts`,
  ];
  const loader = candidates.map(key => screenModules[key]).find(Boolean);
  if (loader) return loader();
  throw new Error(`lazyScreen: no module found for "${componentPath}"`);
});
